import { useEffect, useState } from 'react';

interface HeroStat {
  value: string;
  label: string;
}

interface HeroProps {
  eyebrow: string;
  headline: React.ReactNode;
  subheadline: string;
  cta1: { text: string; href: string };
  cta2: { text: string; href: string };
  stats: HeroStat[];
  backgroundImage: string;
  backgroundAlt?: string;
}

export default function SectionHero({
  eyebrow,
  headline,
  subheadline,
  cta1,
  cta2,
  stats,
  backgroundImage,
  backgroundAlt = 'Ace Diving Miami',
}: HeroProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-end overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt={backgroundAlt}
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#020C1B]/98 via-[#050D1A]/90 to-[#0A1628]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020C1B]/95 via-[#020C1B]/20 to-[#020C1B]/55" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 80% at 15% 50%, rgba(0,180,216,0.04) 0%, transparent 70%)' }} />
      </div>

      {/* Decorative thin lines — left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#00B4D8]/30 to-transparent hidden xl:block" />

      {/* Agency badges — floating right */}
      <div className="absolute top-32 right-8 hidden xl:flex flex-col gap-2 z-10">
        {[
          { label: 'PADI', sub: 'Certified Center' },
          { label: 'SSI', sub: 'Dive Center' },
          { label: 'SDI', sub: 'Training Facility' },
          { label: 'TDI', sub: 'Technical Diving' },
          { label: 'ERDI', sub: 'Public Safety' },
        ].map((badge) => (
          <div key={badge.label} className="flex items-center gap-2.5 bg-white/5 border border-white/12 rounded-full pl-2 pr-4 py-1.5 backdrop-blur-md hover:bg-white/10 transition-all cursor-default">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00B4D8]/20 shrink-0">
              <span className="text-[#00B4D8] text-[9px] font-black">{badge.label[0]}</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-white text-[11px] font-bold tracking-wide">{badge.label}</span>
              <span className="text-white/40 text-[9px] mt-0.5 tracking-wider">{badge.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main content — anchored to bottom */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-44 pb-20">
        {/* Eyebrow row */}
        <div
          className={`flex items-center gap-4 mb-9 transition-all duration-1000 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#00B4D8]" />
            <span className="text-[#00B4D8] text-[11px] font-black uppercase tracking-[0.3em]">{eyebrow}</span>
            <div className="h-px w-8 bg-[#00B4D8]/40" />
          </div>
        </div>

        {/* Headline */}
        <h1
          className={`text-[52px] md:text-[64px] lg:text-[80px] xl:text-[90px] font-black text-white leading-[0.95] mb-8 tracking-tight transition-all duration-1000 delay-100 ease-out max-w-[700px] ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {headline}
        </h1>

        {/* Subheadline */}
        <p
          className={`text-white/85 text-lg md:text-[19px] leading-relaxed max-w-[520px] mb-12 transition-all duration-1000 delay-200 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {subheadline}
        </p>

        {/* CTA group */}
        <div
          className={`flex flex-col sm:flex-row gap-4 mb-20 transition-all duration-1000 delay-300 ease-out ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a
            href={cta1.href}
            className="inline-flex items-center justify-center gap-3 bg-[#00B4D8] hover:bg-[#00C9F0] text-white font-bold px-10 py-4.5 rounded-full transition-all duration-300 hover:-translate-y-1 text-[15px] whitespace-nowrap cursor-pointer group"
            style={{ paddingTop: '18px', paddingBottom: '18px' }}
          >
            {cta1.text}
            <span className="w-5 h-5 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 transition-all shrink-0">
              <i className="ri-arrow-right-line text-xs" />
            </span>
          </a>
          <a
            href={cta2.href}
            className="inline-flex items-center justify-center gap-2.5 border-2 border-white/20 hover:border-[#00B4D8]/70 text-white/70 hover:text-white font-semibold px-10 rounded-full transition-all duration-300 text-[15px] whitespace-nowrap cursor-pointer"
            style={{ paddingTop: '18px', paddingBottom: '18px' }}
          >
            <i className="ri-phone-line text-sm text-[#00B4D8]" />
            {cta2.text}
          </a>
        </div>

        {/* Stats bar */}
        {stats.length > 0 && (
          <div
            className={`flex flex-wrap items-stretch gap-0 transition-all duration-1000 delay-500 ease-out ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {stats.map((stat, i) => (
              <div key={stat.label} className="flex items-center">
                <div className="pr-8 lg:pr-12">
                  <div
                    className="text-[38px] lg:text-[44px] font-black text-[#00B4D8] leading-none tracking-tight"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-white/35 text-[10px] font-bold uppercase tracking-[0.2em] mt-1.5">
                    {stat.label}
                  </div>
                </div>
                {i < stats.length - 1 && (
                  <div className="self-stretch w-px bg-white/10 mr-8 lg:mr-12 hidden sm:block" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 right-10 flex flex-col items-center gap-2 z-10">
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-5 h-8 border border-white/20 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-1 h-1.5 bg-[#00B4D8] rounded-full animate-bounce" />
          </div>
          <span className="text-white/20 text-[9px] uppercase tracking-[0.3em] rotate-90 origin-center mt-1">Scroll</span>
        </div>
      </div>

      {/* Bottom overlap fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F9F7F4] via-transparent to-transparent pointer-events-none opacity-0" />
    </section>
  );
}
